/* <kp-carousel> — native scroll-snap carousel (swipe, keyboard, dots, arrows).
   Markup:
     <kp-carousel [data-autoplay="7"]>
       <div class="kp-carousel__track" data-kp-track> <div class="kp-carousel__slide">…</div> … </div>
       <button data-kp-prev>…</button> <button data-kp-next>…</button>
       <div data-kp-dots></div>
     </kp-carousel>
   Autoplay (opt-in) pauses on hover, focus, touch and when reduced motion is set. */
(() => {
  if (customElements.get('kp-carousel')) return;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class KpCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-kp-track]');
      if (!this.track) return;
      this.slides = [...this.track.children];
      this.dotsEl = this.querySelector('[data-kp-dots]');
      this.prev = this.querySelector('[data-kp-prev]');
      this.next = this.querySelector('[data-kp-next]');
      this.index = 0;
      if (this.slides.length < 2) {
        this.classList.add('is-single');
        return;
      }
      this.buildDots();
      this.prev?.addEventListener('click', () => this.go(this.index - 1));
      this.next?.addEventListener('click', () => this.go(this.index + 1));
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.raf);
        this.raf = requestAnimationFrame(() => this.sync());
      }, { passive: true });
      this.track.addEventListener('keydown', (e) => {
        const direction = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + direction); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - direction); }
      });
      this.sync();
      this.setupAutoplay();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    buildDots() {
      if (!this.dotsEl) return;
      const label = this.dotsEl.dataset.label || 'Slide';
      this.dotsEl.innerHTML = '';
      this.slides.forEach((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'kp-carousel__dot';
        b.setAttribute('aria-label', `${label} ${i + 1}`);
        b.addEventListener('click', () => this.go(i));
        this.dotsEl.appendChild(b);
      });
    }

    slideWidth() {
      return this.slides[0].getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 0);
    }

    go(i) {
      const max = this.slides.length - 1;
      const target = i > max ? 0 : i < 0 ? max : i;
      const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
      this.track.scrollTo({ left: target * this.slideWidth() * rtl, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    sync() {
      const i = Math.round(Math.abs(this.track.scrollLeft) / this.slideWidth());
      this.index = Math.max(0, Math.min(i, this.slides.length - 1));
      this.slides.forEach((s, n) => {
        const current = n === this.index;
        s.toggleAttribute('inert', !current && this.hasAttribute('data-single-view'));
        s.setAttribute('aria-hidden', String(!current && this.hasAttribute('data-single-view')));
      });
      if (this.dotsEl) [...this.dotsEl.children].forEach((d, n) => d.setAttribute('aria-current', String(n === this.index)));
    }

    setupAutoplay() {
      const seconds = parseInt(this.dataset.autoplay, 10);
      if (!seconds || reduceMotion.matches) return;
      let paused = false;
      const pause = () => { paused = true; };
      const resume = () => { paused = false; };
      this.addEventListener('mouseenter', pause);
      this.addEventListener('mouseleave', resume);
      this.addEventListener('focusin', pause);
      this.addEventListener('focusout', resume);
      this.addEventListener('touchstart', pause, { passive: true });
      this.timer = setInterval(() => {
        if (!paused && !document.hidden) this.go(this.index + 1);
      }, seconds * 1000);
    }
  }

  customElements.define('kp-carousel', KpCarousel);

  /* Product rows (.kp-row): desktop arrow buttons scroll by one "page". */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-kp-row-prev], [data-kp-row-next]');
    if (!btn) return;
    const row = document.getElementById(btn.getAttribute('aria-controls'));
    if (!row) return;
    const dir = (btn.hasAttribute('data-kp-row-next') ? 1 : -1) * (getComputedStyle(row).direction === 'rtl' ? -1 : 1);
    row.scrollBy({ left: dir * row.clientWidth * 0.9, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  });
})();
